import React from 'react';
import { format, parseISO, differenceInDays, addDays, addWeeks, addMonths, addQuarters, addYears, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear } from 'date-fns';
import { withRemote } from './Utils/Utils'
import { getStyle as convertTemplateStyle } from './Utils/styleConversion';
import { AlertCircle } from 'react-feather';
import { PageContentBodyGrid } from './layouts/Shared/Page/Page';
import PageGridItem from './layouts/Shared/Page/PageGridItem';

//Mirrors PDFBuilder.applyDateAdjusters: comma-separated chain after the quoted pattern,
//e.g. DateTimeFormatter'MMMM yyyy'-1Q,endOfQuarter (adjusters must not contain ':')
export const applyDateAdjusters = (date, adjusterSpec) => {
	if (!adjusterSpec) return date;
	adjusterSpec.split(",").forEach(adjuster => {
		adjuster = adjuster.trim();
		if (!adjuster) return;
		const offset = adjuster.match(/^([+-])(\d+)([dwmqy])$/i);
		if (offset) {
			let amount = parseInt(offset[2], 10);
			if (offset[1] === "-") amount = -amount;
			switch (offset[3].toLowerCase()) {
				case "d": date = addDays(date, amount); break;
				case "w": date = addWeeks(date, amount); break;
				case "m": date = addMonths(date, amount); break;
				case "q": date = addQuarters(date, amount); break;
				case "y": date = addYears(date, amount); break;
				default: break;
			}
		} else switch (adjuster) {
			case "startOfMonth": date = startOfMonth(date); break;
			case "endOfMonth": date = endOfMonth(date); break;
			case "startOfQuarter": date = startOfQuarter(date); break;
			case "endOfQuarter": date = endOfQuarter(date); break;
			case "startOfYear": date = startOfYear(date); break;
			case "endOfYear": date = endOfYear(date); break;
			default: throw new Error("Unknown date adjuster: " + adjuster);
		}
	});
	return date;
};

export const applyDateFormat = (quantity, dateFormat) => {
	if (dateFormat.includes("DateTimeFormatter")) {
		const quoteStart = dateFormat.indexOf("'");
		const quoteEnd = dateFormat.lastIndexOf("'");
		const hasQuotedPattern = quoteStart >= 0 && quoteEnd > quoteStart;
		const pattern = hasQuotedPattern
			? dateFormat.substring(quoteStart + 1, quoteEnd)
			: dateFormat.substring("DateTimeFormatter".length);
		const adjusterSpec = hasQuotedPattern ? dateFormat.substring(quoteEnd + 1) : "";
		if (/^[qQ]+$/.test(pattern)) {
			let month;
			if (adjusterSpec) {
				const date = applyDateAdjusters(parseISO(quantity), adjusterSpec);
				if (isNaN(date.getTime())) return quantity;
				month = date.getMonth() + 1;
			} else {
				if (!quantity || typeof quantity !== 'string' || quantity.length < 7) return quantity;
				month = parseInt(quantity.substring(5, 7), 10);
			}
			if (isNaN(month)) return quantity;
			const q = Math.ceil(month / 3);
			const len = pattern.length;
			if (len === 1) return String(q);
			if (len === 2) return String(q).padStart(2, '0');
			if (len === 3) return 'Q' + q;
			return ['1st', '2nd', '3rd', '4th'][q - 1] + ' quarter';
		}
		return format(applyDateAdjusters(parseISO(quantity), adjusterSpec), pattern
			.replace(/q/g, 'Q')
			.replace(/u+/g, m => 'y'.repeat(m.length))   // proleptic year → calendar year (uu→yy gives 2-digit)
			.replace(/w+/g, m => 'I'.repeat(m.length)),  // ISO week of year (Java w = ISO, date-fns w = locale)
			{ useAdditionalDayOfYearTokens: true });      // allows D (day of year)
	}
	// date-fns MMMMM is narrow (single letter); Java treats 4+ Ms as full month, so collapse 5 Ms to date-fns full
	let pattern = dateFormat;
	if (pattern.includes("MMMMM")) {
		pattern = pattern.replaceAll('MMMMM', "MMMM");
	}
	return format(parseISO(quantity), pattern);
};

// Exported so read-only snapshot providers (ComparePage) can supply their own Data
// instance to content components; everything else should keep using withData.
export const DataContext = React.createContext({ blah: "blah1" });

export function withData(Child, key) {
	return function DataChild(props) {
		return React.createElement(DataContext.Consumer, null, function (data) {
			return React.createElement(Child, Object.assign({}, props, {
				data: data,
				key: key + data?.localContentData?.length
			}));
		});
	};
}


//const JET = window.JET;
const workingInDev = false;

// Exported for ComparePage's per-pane snapshot instances (see layouts/ComparePage/snapshotData.js).
export class Data {

	constructor(props) {
		this.props = props;
		this.setRemote = this.setRemote.bind(this);
		this.traverse = this.traverse.bind(this);
		this.compileData = this.compileData.bind(this);
	}

	isBroker = false;
	contentData = {};
	listeners = [];
	localContentData = {};
	localContentLoaded = false;
	localListeners = [];
	tickerListeners = [];
	linkedGroupContext = null;

	setLinkedGroupContext(ctx) {
		// ctx = { fundNumbers: string[], linked: boolean } | null
		this.linkedGroupContext = ctx;
	}

	setRemote(remote) {
		this.dataProvider = remote;
	}

	/**
	 * Lookup the actual value of a defined field.
	 * @param {*} val The value to lookup from the stored fields 
	 * @param {*} dataSuffix Suffix to append on the lookup if required. 
	 * @returns 
	 */
	getValue(val, dataSuffix, localContentData) {
		val = this.isBroker && val.brokerValue ? val.brokerValue : val.value;
		if (!val)
			return val;

		if (dataSuffix && this.tickers.length == 1) {
			dataSuffix = null;
		}

		if (val === "${FLOWING_LAST_PAGE}") {
			const pageBreaksSrting = localContentData && localContentData["MainBodyPageBreaks"]
			const pageBreaks = pageBreaksSrting ? JSON.parse(pageBreaksSrting) : [];
			return pageBreaks.length > 0 ? (pageBreaks.length + 1).toString() : "1";
		}

		if (val.includes("PAGE_NUM")) {
			//console.log("### this.contentData", this.contentData)
			//todo figure out page numbers
			return "";
		}
		
		const self = this;
		
		return val.replace(/\$\{(.*?)\}/g, function (match, quantity) {
			//quantity = quantity.substring
			let errorValue = null;
			if (quantity.startsWith("coalesce")) {
				const end = quantity.indexOf(")");
				errorValue = quantity.substring(9, end);
				quantity = quantity.substring(end + 2);
			}

			if (quantity.startsWith("date:")) {
				quantity = format(new Date(), quantity.substring(5));
				return quantity;

			} if (quantity.startsWith("datediff")) {
				const arr1 = quantity.split("datediff(")
				const arr2 = arr1[1].split(",")
				const arr3 = arr2[1].split(")")
				const arr4 = arr3[1].split(" ")
				const date1 = self.contentData[arr2[0].toUpperCase()]
				const date2 = self.contentData[arr3[0].toUpperCase()]
				const theDiff = differenceInDays(new Date(date1), new Date(date2))
				const theParam = +arr4[2]
				const isHigherSymbol = arr4[1] === '>'
				const theFormula = isHigherSymbol ? theDiff > theParam : theDiff < theParam
				quantity = theFormula

			} else {
				const upper = quantity.startsWith("uppercase:");
				if (upper) {
					quantity = quantity.substring(10);
				}
				let dateFormat = null;
				if (quantity.startsWith("formatDate:")) {
					const index = quantity.indexOf(":", 12);
					dateFormat = quantity.substring(11, index);
					quantity = quantity.substring(index + 1);
				}
				//Literal "date" means the current date (e.g. ${formatDate:...:date} or bare ${date}); a real content field keyed DATE takes precedence
				if (quantity === "date" && self.contentData["DATE" + (dataSuffix ? dataSuffix : "")] === undefined) {
					const isoDate = format(new Date(), "yyyy-MM-dd");
					if (!dateFormat) {
						return isoDate;
					}
					try {
						return applyDateFormat(isoDate, dateFormat);
					} catch (e) {
						return quantity;
					}
				}
				let multiplierChar = "1";
				let numberFormat = null;
				if (quantity.startsWith("format:")) {
					multiplierChar = quantity.substring(7, 8);
					const index = quantity.indexOf(":", 9);
					numberFormat = quantity.substring(9, index);
					quantity = quantity.substring(index + 1);
				}
				const bits = new Set();
				quantity = quantity.toUpperCase();
				self.parseComplexQuantity(quantity, bits);
				let processed = false;
				if (bits.size === 1) {
					bits.forEach(q => {
						if (q === quantity) {
							//It's a simple
							quantity = self.contentData[q + (dataSuffix ? dataSuffix : "")];
							processed = true;
							if (dateFormat) {
								try {
									quantity = applyDateFormat(quantity, dateFormat);
									dateFormat = null;	//applied — don't re-apply below
								} catch (e) {
									// console.log("unable to parse iso date ", quantity)
								}
							}
						}
					});
				}
				if (!processed) {
					//Calculation
					let script = "";
					bits.forEach(bit => {
						let value = self.contentData[bit + (dataSuffix ? dataSuffix : "")];
						//eslint disabled due to bug bug - see note below
						const sanitisedBit = bit.replace(/[\[\-\]\.]/g, "_");					//eslint-disable-line no-useless-escape
						if (isNaN(value)) {
							value = "'" + value + "'";
						}
						script += "let " + sanitisedBit + " = " + value + ";"

						//Also santise the original
						if (sanitisedBit !== bit) {  //Shouldn't be necessary, but if it is we get an infinite loop so better to be safe than sorry
							while (quantity.indexOf(bit) > -1)
								quantity = quantity.replace(bit, sanitisedBit);
						}
					});
					script += "quantity = " + quantity;
					// console.log("evaling ", script)
					try {
						eval(script);
					} catch (e) {
						quantity = errorValue || "-";	//unable to evaluate
					}
				}

				if (typeof quantity === "number" && isNaN(quantity)) {
					quantity = errorValue || "";
				} else {
					if (upper) {
						quantity = quantity && quantity.toUpperCase();
					}
					if (dateFormat && !(quantity === 'N/A')) {
						try {
							quantity = applyDateFormat(quantity, dateFormat);
						} catch (e) {
							// console.log("unable to parse iso date ", quantity)
						}
					}
					if (numberFormat) {
						switch (multiplierChar.toUpperCase()) {
							case "k":
								quantity /= 1000;
								break;
							case "M":
								quantity /= 1000000;
								break;
							case "B":
								quantity /= 1000000000;
								break;
							case "Z":
								if (Math.abs(quantity) > 1000) {
									quantity /= 1000;
									if (Math.abs(quantity) > 1000) {
										quantity /= 1000;
										if (Math.abs(quantity) > 1000) {
											quantity /= 1000;
											return new DecimalFormat(numberFormat).format(quantity) + "B";	//eslint-disable-line no-undef
										} else {
											return new DecimalFormat(numberFormat).format(quantity) + "M" 	//eslint-disable-line no-undef
										}
									} else {
										return new DecimalFormat(numberFormat).format(quantity) + "k";		//eslint-disable-line no-undef
									}
								} else {
									return new DecimalFormat(numberFormat).format(quantity);				//eslint-disable-line no-undef
								}
							default:
								break;
						}
						quantity = new DecimalFormat(numberFormat).format(quantity);  //eslint-disable-line no-undef
					}
				}
			}
			return quantity;
		});
	}

	getLocalValue(prop) {
		return this.localContentData[prop];
	}

	// Delegates to the shared conversion (Utils/styleConversion.js) — the Raw slate
	// wrappers use the same function, so editor and compare render identically.
	getStyle(style, includeCellStyles) {
		return convertTemplateStyle(style, includeCellStyles);
	}

	//FIXME - hardcoded, need a generic way of getting localdata for groups
	updateOfferName(name) {
		const self = this;
		if (self.contentData) {
			self.contentData.OFFERNAME = name;
		}
	}

	async updateTickerGroup(name, allTickers, getGroupData) {
		this.tickerGroup = name;
		this.allTickers = allTickers;
		this.contentData["TICKERGROUP"] = name;
		const self = this;
		const runAfter = () => {
			// Persist TICKERGROUP only when it differs from what's stored — an
			// unconditional save here minted a version row on every editor open.
			// On a never-saved doc, skip the null→value backfill too: it must not
			// mint the document's FIRST version row (see setTickers).
			const stored = self.storedIdentityData;
			if (stored && stored.TICKERGROUP !== name
				&& (stored.TICKERGROUP != null || self.storedDocExists)) {
				self.updateLocalData("TICKERGROUP", name);
			}
			self.loadConsensusData.bind(self, getGroupData)(true);
			// self.loadConsensusData.bind(self)(true)
		}
		const promise = this.loadLocalData.bind(this)({ "TICKERGROUP": name }, runAfter);
		if (self.contentData) {
			self.contentData.TICKERGROUP = name;
		}
		return await promise;
		//TODO trigger a consensus lookup too
	}

	registerTemplate(template, templateName) {
		this.templateName = templateName;
		const consensusFields = new Set();
		const brokerFields = new Set();
		const indexFields = new Set();

		//Traverse the tree
		template.pages.forEach(page => {
			page.content.forEach(content => {
				this.traverse(content, consensusFields, indexFields, brokerFields);
			});
		});
		this.templateType = template.ticker.type;

		this.consensusFields = [];
		consensusFields.forEach(field => this.consensusFields.push(field));
		this.brokerFields = [];
		brokerFields.forEach(field => this.brokerFields.push(field));
		this.indexFields = [];
		indexFields.forEach(field => this.indexFields.push(field));

		//Also clear local data
		this.localContentData = {};
		this.localContentLoaded = false;
		this.tickers = [""];
		this.tickerGroup = null;
		this.fireLocalUpdate.bind(this)();
		this.loadLocalData();
	}

	setTickers(tickers, index, broker) {  //TODO add broker and index
		if (!tickers) {
			return;
		}
		this.localContentData = {}
		this.tickers = tickers;
		this.index = index;
		this.broker = broker;
		const tickerGroup = this.contentData.TICKERGROUP;
		this.contentData = {};
		if (tickerGroup) {
			this.contentData.TICKERGROUP = tickerGroup;
		}
		const tickerList = tickers.join(', ');
		this.contentData["TICKERLIST"] = tickerList;

		this.localContentLoaded = false
		//Initially load consensus data
		this.loadConsensusData.bind(this)();
		//and local data; persist TICKERLIST only when it differs from what's stored —
		//an unconditional save here minted a version row on every editor open.
		//On a NEVER-SAVED doc (nothing stored at all) skip the null→value backfill
		//too: it would mint the document's first version row and enable Compare on
		//a doc nobody authored. Docs with stored content still get the one-time
		//backfill — the publish path reads stored TICKERLIST to expand multi-ticker
		//documents (createPublishDTO falls back to single-ticker without it).
		const self = this;
		this.loadLocalData.bind(this)({ "TICKERLIST": tickerList }, () => {
			const stored = self.storedIdentityData;
			if (stored && stored.TICKERLIST !== tickerList
				&& (stored.TICKERLIST != null || self.storedDocExists)) {
				self.updateLocalData("TICKERLIST", tickerList);
			}
		});
		this.fireTickerUpdate.bind(this)();
	}

	getTickers() {
		return this.tickers;
	}

	getTemplateName() {
		return this.templateName;
	}

	compileData(into) {
		into = into || {};
		Object.assign(into, this.contentData, this.localContentData);
		return into;
	}

	/**
	 * Update the tickers in the controller
	 * @param {An array of strings containing the tickers} tickers 
	 */
	updateTicker(tickers, src) {
		this.setTickers(tickers);
		this.localContentData = {};
		this.localContentLoaded = false;
		// The caller has named the tickers explicitly — a TICKERLIST persisted
		// on the row (e.g. a group primary's number bulk-written to a member)
		// must not override them, or every subsequent save silently targets
		// the wrong ticker. Same override setTickers() itself passes.
		this.loadLocalData({ "TICKERLIST": tickers.join(', ') });
		const localUpdatePromise = this.fireLocalUpdate.bind(this)(src);
		this.fireTickerUpdate.bind(this)();

		return localUpdatePromise;
	}


	async loadConsensusData(fireTickerUpdate, getGroupData) {
		// async loadConsensusData(fireTickerUpdate) {
		if (!this.consensusFields || this.consensusFields.length === 0)
			return;

		const self = this;
		//console.log("Constructing the request, instruments is ", this)
		const instruments = getGroupData ? [self.tickerGroup] : (self.tickers && self.tickers.length > 0 && self.tickers[0] !== "") ? self.tickers : self.allTickers;
		//const instruments = (self.tickers && self.tickers.length > 0 && self.tickers[0] !== "") ? self.tickers : self.allTickers;
		const request = {
			instruments: instruments,
			base: self.tickerGroup,
			fields: self.consensusFields.map(field => { return { name: field } })
		};

		await this.dataProvider.request(request).then(resp => {
			const consensusData = JSON.parse(resp);
			const headers = consensusData.headers;
			const data = consensusData.data;
			for (let i = 1; i < headers.length; i++) {
				self.contentData[headers[i].field.toUpperCase()] = data[i];
			}
			if (self.localContentData && self.localContentData.TICKERGROUP) {
				self.contentData.TICKERGROUP = self.localContentData.TICKERGROUP;
			}
			if (self.localContentData && self.localContentData.TICKERLIST) {
				self.contentData.TICKERLIST = self.localContentData.TICKERLIST;
				if (fireTickerUpdate) {
					self.fireTickerUpdate.bind(this);
				}
			}
			self.fireUpdate.bind(self)();
		});
	}

	loadLocalData(overrides, then) {
		const self = this;
		let identifier = this.tickers ? this.tickers.join(',') : '';
		if (this.templateType === 'selection' || this.templateType === 'scheme') {
			identifier = this.tickerGroup;
		}
		if (!identifier || identifier === '') {
			if (then) {
				then();
			}
			return new Promise(resolve => resolve(false));
		}
		identifier = btoa(identifier);
		self.localContentLoaded = false
		self.storedIdentityData = null;
		self.storedDocExists = false;
		return this.props.remote.get("/retrievev2/" + identifier + "/" + this.templateName)
			.then(localData => {
				// Identity metadata as PERSISTED, captured before overrides stomp it —
				// setTickers/updateTickerGroup diff against this so a plain editor open
				// (unchanged TICKERLIST/TICKERGROUP) doesn't fire a savev2 and mint a
				// phantom version row.
				self.storedIdentityData = { TICKERLIST: localData.TICKERLIST, TICKERGROUP: localData.TICKERGROUP };
				// Whether ANY data is stored for this document — a pure identity
				// backfill (null → value) must not mint the document's FIRST version
				// row, or Compare enables on never-authored docs.
				self.storedDocExists = Object.keys(localData).length > 0;
				if (overrides) {
					Object.assign(localData, overrides);
				}
				if (localData.TICKERLIST) {
					this.tickers = localData.TICKERLIST.split(", ");
					self.fireTickerUpdate.bind(self)();
				}
				self.localContentData = localData;
				self.localContentLoaded = true;
				self.dataProvider.topMessageSetter(null)
				self.fireLocalUpdate.bind(self)().then(e => {
					if (then) {
						then();
					}
					return new Promise(resolve => resolve(true));
				})
			});
	}

	temporaryUpdateLocalData(field, data, src, dataName) {
		//console.log("# temporaryUpdateLocalData field", field);
		//console.log("# temporaryUpdateLocalData data", data);
		this.localContentData[field] = data;
	}
	
	updateLocalData(field, data, src, dataName) {
		//console.log("# updateLocalData field", field);
		//console.log("# updateLocalData data", data);
		const self = this;
		if (!self.tickers || self.tickers.length < 1) {
			//can't save anything without a ticker!
			this.dataProvider.topMessageSetter({ noDocument: true })
			return;
		}
		if (!self.templateName) {
			this.dataProvider.topMessageSetter({ noDocument: true })
			//can't save anything without a ticker!
			return;
		}
		if (!self.localContentLoaded) {
			this.dataProvider.topMessageSetter({ noDocument: true })
			// don't override incoming data
			return;
		}

		let identifier = self.tickers.join(',');
		if (self.templateType === 'selection' || self.templateType === 'scheme') {
			identifier = this.tickerGroup;
		}
		if (!identifier || identifier === '') {
			this.dataProvider.topMessageSetter({ noDocument: true })
			return;
		}
		this.dataProvider.topMessageSetter({ saving: true })
		const obj = {};
		obj[field] = data;
		const ctx = self.linkedGroupContext;
		// TICKERLIST is per-ticker identity metadata — fanning it out would stamp
		// the primary's number onto every member row, which then hijacks the
		// ticker when a member is later loaded on its own.
		const bulkLinked = field !== 'TICKERLIST'
			&& ctx && ctx.linked && Array.isArray(ctx.fundNumbers) && ctx.fundNumbers.length > 1;
		const savePromise = bulkLinked
			? this.props.remote.put("/savev2-bulk/" + self.templateName, { tickers: ctx.fundNumbers, data: obj })
			: this.props.remote.put("/savev2/" + btoa(identifier) + "/" + self.templateName, obj);
		savePromise.then(response => {
			if (response.success) {
				self.fireSaveSucceeded(response.data);
			}
			if (!response.success) {
				self.fireSaveError();
			}
		}).catch(err => {
			console.log("Catch response, ", err);
			self.fireSaveError();
		});
		this.localContentData[field] = data;
		if (src) {
			this.fireLocalUpdate(src, dataName);
		}
	}

	getVersions(limit) {
		const versionBase = this.getVersionBase();
		if (versionBase) {
			return this.props.remote.get(versionBase + "?limit=" + limit);
		} else {
			return Promise.resolve([]);
		}
	}

	getVersionBase() {
		const self = this;
		if (!self.tickers || self.tickers.length < 1) {
			return null;
		}
		if (!self.templateName) {
			return null;
		}
		let identifier = self.tickers.join(',');
		if (self.templateType === 'selection' || self.templateType === 'scheme') {
			identifier = this.tickerGroup;
		}
		if (!identifier || identifier === '') {
			return null;
		}
		identifier = btoa(identifier);
		return "/versions/" + identifier + "/" + self.templateName;
	}

	deleteVersion(id, num) {
		const base = this.getVersionBase();
		if (!base) {
			return Promise.resolve({ success: false });
		}
		return this.props.remote.delete(base + "/" + id + "?limit=" + num);
	}

	restoreVersion(id) {
		const base = this.getVersionBase();
		if (!base) {
			return Promise.resolve({ success: false });
		}
		return this.props.remote.post(base, { version: id });
	}

	lockVersion(id, lock, num) {
		const base = this.getVersionBase();
		if (!base) {
			return Promise.resolve({ success: false });
		}
		return this.props.remote.put(base + "/" + id, { lock: lock, limit: num });
	}

	setSaveErrorListener(listener) {
		this.saveErrorListener = listener;
	}

	setSaveSizeListener(listener) {
		this.saveSizeListener = listener;
	}

	unsetSaveSizeListener() {
		this.saveSizeListener = null;
	}

	fireSaveError() {
		this.dataProvider.topMessageSetter({ error: true })
		if (this.saveErrorListener) {
			this.saveErrorListener(true);
		}
	}
	fireSaveSucceeded(size) {
		this.dataProvider.topMessageSetter({ lastSaved: new Date() })
		if (this.saveErrorListener) {
			this.saveErrorListener(false);
		}
		if (this.saveSizeListener) {
			this.saveSizeListener(size);
		}
	}

	fireUpdate() {
		this.listeners.forEach(list => {
			list();
		});
	}

	fireLocalUpdate(src, dataName) {
		return Promise.all(this.localListeners.map(list => list(src, dataName)))
		// this.localListeners.forEach(list=>{
		// 	list(src, dataName).then(console.log("Local listener finished"));
		// });
	}

	fireTickerUpdate() {
		this.tickerListeners.forEach(list => {
			list(this.tickers);
		});
	}

	addLocalListener(list) {
		this.localListeners.push(list);
	}

	removeLocalListener(list) {
		this.localListeners = this.localListeners.filter(l => l !== list);
	}

	addListener(list) {
		this.listeners.push(list);
	}

	removeListener(list) {
		this.listeners = this.listeners.filter(l => l !== list);
	}

	removeTickerListener(list) {
		this.tickerListeners = this.listeners.filter(l => l !== list);
	}

	addTickerListener(list) {
		this.tickerListeners.push(list);
	}

	/**
	 * Traverses an object, populating fields with all required fields.
	 * 
	 * @param {*} obj The object to recursively traverse 
	 * @param {Set} consensusFields  set to populate
	 * @param {Set} indexFields 
	 * @param {Set} brokerFields 
	 */
	traverse(obj, consensusFields, indexFields, brokerFields) {

		const self = this;
		//We'll just look for any string with a matching field(s)
		Object.keys(obj).forEach(key => {
			const val = obj[key];
			if (typeof val === "object" && val !== null && val !== undefined) {
				this.traverse(val, consensusFields, indexFields, brokerFields);
			} else if (typeof val === "string") {
				let target = null;
				if (key === "value") {
					target = consensusFields;
				} else if (key === "brokerValue") {
					target = brokerFields;
				}
				if (target !== null) {
					val.replace(/\$\{(.*?)\}/g, function (match, quantity) {
						//Remove the ${ }
						//quantity = quantity.substring
						if (quantity.startsWith("date:")) {
							//Do nothing
						} else {
							if (quantity.startsWith("index:")) {
								indexFields.add(quantity.substring(6));
							} else {
								//TODO format number quantities
								if (quantity.startsWith("risk:"))
									quantity = quantity.substring(5);

								if (quantity.startsWith("uppercase"))
									quantity = quantity.substring(10);

								if (quantity.startsWith("formatDate:")) {
									const index = quantity.indexOf(":", 12);
									quantity = quantity.substring(index + 1);
								}
								if (quantity === "date") {
									//Literal current-date source, not a data field
									return quantity;
								}

								if (quantity.startsWith("coalesce")) {
									const index = quantity.indexOf(":", 7);
									quantity = quantity.substring(index + 1);
								}

								if (quantity.startsWith("format:")) {
									const index = quantity.indexOf(":", 10);
									quantity = quantity.substring(index + 1);
								}
								//Handle Calculations
								self.parseComplexQuantity(quantity, target);
							}
						}
						return quantity;
					});
				}
			}
		});
	}

	parseComplexQuantity(quantity, target) {
		//NB:  eslint reports no-useless-escape when the escape is _not_ useless.  These regexs are correct :)
		//Firstly remove any - that are part of qualifiers
		quantity = quantity.replace(/(\[[^\]]*?)\-([^\]]*?\])/g, "$1#$2")			//eslint-disable-line no-useless-escape

		//remove any quoted string literals.  Currently only handles exactly one.
		if (quantity.indexOf('\'') > -1) {
			let original = quantity;
			let firstQuote = quantity.indexOf('\'');
			let secondQuote = quantity.indexOf('\'', firstQuote + 1);
			if (secondQuote > -1) {
				quantity = original.substring(0, firstQuote);
				quantity += original.substring(secondQuote + 1, original.length);
			}
		}
		//Now split by operators
		const bits = quantity.split(/[\*\+\-\/\(\)\s\,\=]+/);							//eslint-disable-line no-useless-escape
		bits.forEach(bit => {
			//Empty?
			if (bit.length === 0)
				return;
			//Number?
			if (bit.match(/^\-?\d*\.?\d*$/))										//eslint-disable-line no-useless-escape
				return;
			//And put back the -s
			bit = bit.replace(/#/, "-");
			const match = bit.match(/\[(\-?\d+)\~(\-?\d+)\]/);						//eslint-disable-line no-useless-escape
			if (match) {
				const start = match[1];
				const end = match[2];
				for (let i = start; i <= end; i++) {
					target.add(bit.replace(match[0], "[" + i + "]"));
				}
			} else {
				target.add(bit);
			}
		});
	}
}

class DataController extends React.Component {

	constructor(props) {
		super(props);
		this.state = {
			hasError: false
		}
		const { setTopMessage } = props;
		this.provider = {
			request: (data) => {
				//console.log("Data getting all instruments.  Data is ", data)

				const allPromises = data.instruments.map(instrument => {
					return Promise.resolve(this.props.remote.post(`/data/${instrument}/get`, data.fields.map(field => field.name)));
				})
				let hasBase = false;
				if (data.base) {
					//console.log("Have a base of ", data.base)
					hasBase = true;
					allPromises.unshift(Promise.resolve(this.props.remote.post(`/data/${data.base}/get`, data.fields.map(field => field.name))));
				}

				return Promise.all(
					allPromises
				).then(responses => {

					//const json = {data:[], headers:[]};
					const eikonFormat = {
						data: [""],
						headers: [{ field: "" }]
					};
					for (const [index, value] of responses.entries()) {
						for (const [dataIndex] of value.headers.entries()) {
							if (responses.length > 1 && (index > 0 || !hasBase)) {
								const adjustedIndex = hasBase ? index - 1 : index;
								eikonFormat.headers.push({ field: value.headers[dataIndex].field + "<" + data.instruments[adjustedIndex] + ">" });
							} else {
								eikonFormat.headers.push({ field: value.headers[dataIndex].field });
							}
							eikonFormat.data.push(value.data[dataIndex] ? value.data[dataIndex] : "");
						}
					}
					return new Promise(resolve => resolve(JSON.stringify(eikonFormat)));
				})
			},
			topMessageSetter: setTopMessage
		}

		this.data.setRemote(this.provider);
	}

	data = new Data(this.props);

	componentDidCatch(error, info) {
		console.log(error)
		this.setState(state => ({ ...state, hasError: true }))
	}


	render() {
		if (this.state.hasError) {
			return (
				<PageContentBodyGrid rowGap="xl" showScrollbar>
					<PageGridItem colSpan="12">
						<div style={{ "margin": "20px 20px" }}><AlertCircle color={"#E66780"} />
							There has been an unrecoverable error.  To prevent loss of data, all editors have been closed.  Please reload the application to continue.
						</div>
					</PageGridItem>
				</PageContentBodyGrid>
			)
		}


		return <DataContext.Provider value={this.data}>{this.props.children}</DataContext.Provider>
	}

}

export default withRemote(DataController);